/**
 * Compare Products.
 *
 * Column 0 is the product being viewed and never changes. The other columns are
 * filled from a picker with two sources:
 *
 *   - Collection floors, serialised into the element at render. Filtered as the
 *     shopper types, and swapping one in is a DOM write rather than a fetch.
 *   - "More floors", from Shopify's predictive search rendered through
 *     sections/compare-search (floors only). Picking one fetches its comparison
 *     data from /products/{handle}?view=compare-data - the same object the
 *     collection floors carry - then renders it exactly like one of them.
 *
 * Registered as a custom element so it survives the section-rendering swap the
 * colour selector performs - theDomWatcher runs a loader once and never
 * re-observes, so anything bound through it would go dead after a product swap.
 */
const SEARCH_MIN_CHARS = 2;
const SEARCH_DEBOUNCE_MS = 250;
const SEARCH_LIMIT = 10;

function routesRoot() {
  return window.Shopify?.routes?.root || "/";
}

if (!customElements.get("product-compare")) {
  customElements.define(
    "product-compare",
    class ProductCompare extends HTMLElement {
      connectedCallback() {
        if (this.bound) return;
        this.bound = true;

        try {
          this.candidates = JSON.parse(this.dataset.candidates || "[]");
        } catch {
          this.candidates = [];
        }

        this.columns = parseInt(this.dataset.columns, 10) || 3;
        this.selected = new Array(this.columns).fill("");

        // Column 0 is the product being viewed and has no picker at all.
        this.selected[0] = this.dataset.currentHandle || "";

        // Per-column search state: each picker searches independently.
        this.remoteResults = {};
        this.searching = {};
        this.searchErrors = {};
        this.searchTimers = {};
        this.searchControllers = {};

        this.populateMenus();
        this.bindEvents();
        this.syncPickerHeight();
        this.watchLayout();
      }

      disconnectedCallback() {
        this.pickerObserver?.disconnect();
        this.layoutQuery?.removeEventListener("change", this.onLayoutChange);
        document.removeEventListener("click", this.onDocumentClick);
        Object.values(this.searchControllers).forEach((controller) => controller?.abort());
        Object.values(this.searchTimers).forEach((timer) => window.clearTimeout(timer));
      }

      /**
       * Mobile puts the buy controls under the comparison rather than above it.
       * The node is moved rather than duplicated - two Order Sample buttons for
       * one variant would each track their own added/limit state and drift.
       */
      watchLayout() {
        this.layoutQuery = window.matchMedia("(max-width: 991.98px)");
        // The heading sits above the scroller on mobile so it stays put while
        // the table scrolls sideways; on desktop it is the first grid cell.
        this.placeIntro = () => {
          const intro = this.querySelector(".product-compare__intro");
          const slot = this.querySelector(".product-compare__intro-slot");
          const grid = this.querySelector(".product-compare__grid");
          if (!intro || !slot || !grid) return;

          if (this.layoutQuery.matches) {
            if (intro.parentElement !== slot) slot.appendChild(intro);
          } else if (intro.parentElement !== grid) {
            grid.insertBefore(intro, grid.firstElementChild);
          }
        };

        this.placeActions = () => {
          this.querySelectorAll("[data-compare-actions]").forEach((actions) => {
            const col = actions.dataset.compareCol;
            const target = this.layoutQuery.matches
              ? this.querySelector(`[data-compare-actions-slot="${col}"]`)
              : this.querySelector(`.product-compare__col[data-compare-col="${col}"]`);

            if (target && actions.parentElement !== target) target.appendChild(actions);
          });
        };

        this.onLayoutChange = () => {
          this.placeIntro();
          this.placeActions();
        };

        this.onLayoutChange();
        this.layoutQuery.addEventListener("change", this.onLayoutChange);
      }

      /**
       * Every column reserves the same header height as the current product's
       * picker, so the images share a baseline no matter how long a title is.
       * Measured rather than hardcoded: a title wraps to two lines and changes it.
       */
      syncPickerHeight() {
        const reference = this.querySelector(
          ".product-compare__col.is-filled .product-compare__picker",
        );
        if (!reference) return;

        const apply = () => {
          this.style.setProperty(
            "--compare-picker-height",
            `${Math.round(reference.getBoundingClientRect().height)}px`,
          );
        };

        apply();

        if ("ResizeObserver" in window) {
          this.pickerObserver = new ResizeObserver(apply);
          this.pickerObserver.observe(reference);
        }
      }

      columnOf(el) {
        return parseInt(el.closest("[data-compare-col]").dataset.compareCol, 10);
      }

      queryFor(menu) {
        return (menu.querySelector("[data-compare-search]")?.value || "").trim();
      }

      /** Every word of the query has to appear in the title, in any order. */
      matchesQuery(title, query) {
        if (!query) return true;
        const haystack = (title || "").toLowerCase();
        return query
          .toLowerCase()
          .split(/\s+/)
          .every((word) => haystack.includes(word));
      }

      isTakenElsewhere(col, handle) {
        return this.selected.some((h, i) => i !== col && h === handle);
      }

      makeOption(col, handle, title, remote) {
        const option = document.createElement("li");
        option.className = "product-compare__option";
        option.setAttribute("role", "option");
        option.setAttribute("tabindex", "0");
        option.setAttribute("aria-selected", handle === this.selected[col] ? "true" : "false");
        option.dataset.compareOption = handle;
        if (remote) option.dataset.compareRemote = "true";
        option.textContent = title;
        return option;
      }

      /** Every picker gets the same option list, minus anything already chosen. */
      populateMenus() {
        this.querySelectorAll("[data-compare-menu]").forEach((menu) => this.populateMenu(menu));
      }

      populateMenu(menu) {
        const col = this.columnOf(menu);
        const query = this.queryFor(menu);
        const list = menu.querySelector("[data-compare-list]");
        const remoteWrap = menu.querySelector("[data-compare-remote]");
        const remoteList = menu.querySelector("[data-compare-remote-list]");
        const status = menu.querySelector("[data-compare-status]");

        // Collection floors, filtered locally. Rebuilt on every change so a
        // product picked in one column stops being offered in the others.
        let localCount = 0;
        if (list) {
          list.innerHTML = "";
          this.candidates.forEach((candidate) => {
            if (this.isTakenElsewhere(col, candidate.handle)) return;
            if (!this.matchesQuery(candidate.title, query)) return;
            list.appendChild(this.makeOption(col, candidate.handle, candidate.title, false));
            localCount += 1;
          });
        }

        // Search results, minus anything the collection list already offers,
        // the product being viewed, and floors chosen in another column.
        let remoteCount = 0;
        if (remoteList && remoteWrap) {
          remoteList.innerHTML = "";
          const known = new Set(this.candidates.map((c) => c.handle));
          (query.length >= SEARCH_MIN_CHARS ? this.remoteResults[col] || [] : []).forEach(
            (result) => {
              if (!result?.handle || known.has(result.handle)) return;
              if (result.handle === this.selected[0]) return;
              if (this.isTakenElsewhere(col, result.handle)) return;
              remoteList.appendChild(this.makeOption(col, result.handle, result.title, true));
              remoteCount += 1;
            },
          );
          remoteWrap.hidden = remoteCount === 0;
        }

        if (status) {
          let message = "";
          if (this.searchErrors[col]) {
            message = this.dataset.loadErrorLabel || "Couldn't load that floor. Please try again.";
          } else if (this.searching[col] && remoteCount === 0) {
            message = this.dataset.searchingLabel || "Searching…";
          } else if (query && localCount === 0 && remoteCount === 0) {
            message = this.dataset.noResultsLabel || "No floors found";
          }
          status.textContent = message;
          status.hidden = !message;
        }
      }

      openMenu(toggle) {
        this.closeMenus(toggle);
        const menu = toggle.parentElement.querySelector("[data-compare-menu]");
        if (!menu) return;
        menu.hidden = false;
        toggle.setAttribute("aria-expanded", "true");
        toggle.closest("[data-compare-col]").classList.add("is-picking");

        // Focus the search on pointer devices only - on touch it would throw the
        // keyboard up over the list the shopper probably just wants to scroll.
        const input = menu.querySelector("[data-compare-search]");
        if (input && window.matchMedia("(hover: hover)").matches) {
          requestAnimationFrame(() => input.focus());
        }
      }

      closeMenus(except) {
        this.querySelectorAll("[data-compare-toggle]").forEach((toggle) => {
          if (toggle === except) return;
          const menu = toggle.parentElement.querySelector("[data-compare-menu]");
          if (menu) menu.hidden = true;
          toggle.setAttribute("aria-expanded", "false");
          toggle.closest("[data-compare-col]").classList.remove("is-picking");
        });
      }

      /** Clears a picker's search so it reopens on the full collection list. */
      resetSearch(col) {
        const menu = this.querySelector(
          `.product-compare__col[data-compare-col="${col}"] [data-compare-menu]`,
        );
        const input = menu?.querySelector("[data-compare-search]");
        if (input) input.value = "";

        window.clearTimeout(this.searchTimers[col]);
        this.searchControllers[col]?.abort();
        this.remoteResults[col] = [];
        this.searching[col] = false;
        this.searchErrors[col] = false;
      }

      choose(col, handle) {
        this.selected[col] = handle;
        this.renderColumn(col, handle);

        const picker = this.querySelector(
          `.product-compare__col[data-compare-col="${col}"] [data-compare-picker]`,
        );
        const label = picker && picker.querySelector("[data-compare-label]");
        const candidate = this.candidates.find((c) => c.handle === handle);
        if (label)
          label.textContent = candidate
            ? candidate.title
            : this.dataset.selectLabel || "Select a Product to Compare";

        this.resetSearch(col);
        this.populateMenus();
        this.closeMenus();
      }

      /**
       * A floor from search has no data on the page yet, so it is fetched from
       * its compare-data view, added to the candidate pool and then chosen like
       * any other. Once added it also filters locally in every picker.
       */
      async chooseRemote(col, handle, option) {
        if (this.candidates.some((c) => c.handle === handle)) {
          this.choose(col, handle);
          return;
        }

        option?.classList.add("is-loading");
        this.searchErrors[col] = false;

        try {
          const url = `${routesRoot()}products/${encodeURIComponent(handle)}?view=compare-data`;
          const response = await fetch(url, { credentials: "same-origin" });
          if (!response.ok) throw new Error(`compare-data ${response.status}`);

          const candidate = JSON.parse(await response.text());
          if (!candidate?.handle) throw new Error("compare-data returned no product");

          if (!this.candidates.some((c) => c.handle === candidate.handle)) {
            this.candidates.push(candidate);
          }
          this.choose(col, candidate.handle);
        } catch (error) {
          console.error("product-compare:", error);
          option?.classList.remove("is-loading");
          this.searchErrors[col] = true;
          const menu = option?.closest("[data-compare-menu]");
          if (menu) this.populateMenu(menu);
        }
      }

      /** Debounced so a search runs once the shopper pauses, not on every key. */
      scheduleSearch(menu) {
        const col = this.columnOf(menu);
        const query = this.queryFor(menu);

        window.clearTimeout(this.searchTimers[col]);
        this.searchControllers[col]?.abort();
        this.searchErrors[col] = false;

        if (query.length < SEARCH_MIN_CHARS) {
          this.remoteResults[col] = [];
          this.searching[col] = false;
          this.populateMenu(menu);
          return;
        }

        this.searching[col] = true;
        this.populateMenu(menu);
        this.searchTimers[col] = window.setTimeout(
          () => this.runSearch(menu, col, query),
          SEARCH_DEBOUNCE_MS,
        );
      }

      async runSearch(menu, col, query) {
        const controller = new AbortController();
        this.searchControllers[col] = controller;

        const params = new URLSearchParams({
          q: query,
          "resources[type]": "product",
          "resources[limit]": String(SEARCH_LIMIT),
          section_id: "compare-search",
        });

        try {
          const response = await fetch(`${routesRoot()}search/suggest?${params}`, {
            credentials: "same-origin",
            signal: controller.signal,
          });
          if (!response.ok) throw new Error(`search/suggest ${response.status}`);

          const doc = new DOMParser().parseFromString(await response.text(), "text/html");
          const payload = doc.querySelector("[data-compare-search-results]");
          this.remoteResults[col] = payload ? JSON.parse(payload.textContent || "[]") : [];
        } catch (error) {
          if (error?.name === "AbortError") return; // superseded by a newer query
          console.error("product-compare:", error);
          this.remoteResults[col] = [];
        }

        // A result for a query the shopper has since changed is thrown away.
        if (this.queryFor(menu) !== query) return;
        this.searching[col] = false;
        this.populateMenu(menu);
      }

      pick(option) {
        const col = this.columnOf(option);
        if (option.dataset.compareRemote) {
          this.chooseRemote(col, option.dataset.compareOption, option);
        } else {
          this.choose(col, option.dataset.compareOption);
        }
      }

      /** Arrow keys move between the search field and the visible options. */
      moveFocus(menu, from, step) {
        const options = [...menu.querySelectorAll("[data-compare-option]")];
        if (!options.length) return;

        const index = options.indexOf(from);
        if (index === -1) {
          if (step > 0) options[0].focus();
          return;
        }

        const next = index + step;
        if (next < 0) {
          menu.querySelector("[data-compare-search]")?.focus();
        } else if (next < options.length) {
          options[next].focus();
        }
      }

      bindEvents() {
        this.addEventListener("click", (event) => {
          const toggle = event.target.closest("[data-compare-toggle]");
          if (toggle) {
            const isOpen = toggle.getAttribute("aria-expanded") === "true";
            if (isOpen) {
              this.closeMenus();
            } else {
              this.openMenu(toggle);
            }
            return;
          }

          const option = event.target.closest("[data-compare-option]");
          if (option) {
            this.pick(option);
            return;
          }

          // The placeholder is a button so the whole empty slot is a target;
          // it just opens the picker rather than owning any state.
          const placeholder = event.target.closest("[data-compare-open]");
          if (placeholder) {
            const trigger = placeholder
              .closest("[data-compare-col]")
              .querySelector("[data-compare-toggle]");
            if (trigger) this.openMenu(trigger);
            return;
          }

          const dot = event.target.closest("[data-compare-dot]");
          if (dot) this.swapImage(dot);
        });

        this.addEventListener("input", (event) => {
          const input = event.target.closest("[data-compare-search]");
          if (!input) return;
          this.scheduleSearch(input.closest("[data-compare-menu]"));
        });

        this.addEventListener("keydown", (event) => {
          if (event.key === "Escape") {
            this.closeMenus();
            return;
          }

          const menu = event.target.closest("[data-compare-menu]");
          if (menu && (event.key === "ArrowDown" || event.key === "ArrowUp")) {
            event.preventDefault();
            const from = event.target.closest("[data-compare-option]");
            this.moveFocus(menu, from, event.key === "ArrowDown" ? 1 : -1);
            return;
          }

          const input = event.target.closest("[data-compare-search]");
          if (input && event.key === "Enter") {
            // Enter in the field picks the first match rather than doing nothing.
            event.preventDefault();
            const first = menu?.querySelector("[data-compare-option]");
            if (first) this.pick(first);
            return;
          }

          const option = event.target.closest("[data-compare-option]");
          if (option && (event.key === "Enter" || event.key === " ")) {
            event.preventDefault();
            this.pick(option);
          }
        });

        this.onDocumentClick = (event) => {
          if (!this.contains(event.target)) this.closeMenus();
        };
        document.addEventListener("click", this.onDocumentClick);
      }

      swapImage(dot) {
        const media = dot.closest("[data-compare-media]");
        const image = media.querySelector(".product-compare__image");
        if (image && dot.dataset.src) {
          // The current product's image comes from Liquid's `image_tag`, which
          // generates a srcset. The browser picks from that over src, so setting
          // src alone changed nothing — the columns rendered here, which are bare
          // <img src>, were the only ones that ever swapped.
          image.removeAttribute("srcset");
          image.removeAttribute("sizes");
          image.src = dot.dataset.src;
        }

        media
          .querySelectorAll("[data-compare-dot]")
          .forEach((d) => d.classList.toggle("is-active", d === dot));
      }

      renderColumn(col, handle) {
        const column = this.querySelector(`.product-compare__col[data-compare-col="${col}"]`);
        if (!column) return;

        const candidate = this.candidates.find((c) => c.handle === handle);
        const media = column.querySelector("[data-compare-media]");
        const price = column.querySelector("[data-compare-price]");
        const actions = this.querySelector(`[data-compare-actions][data-compare-col="${col}"]`);

        if (!candidate) {
          column.classList.remove("is-filled");
          media.innerHTML = this.placeholderMarkup();
          price.innerHTML = "";
          actions.innerHTML = "";
          this.setValues(col, null);
          return;
        }

        column.classList.add("is-filled");
        media.innerHTML = this.mediaMarkup(candidate);
        price.innerHTML = candidate.priceHtml || "";
        actions.innerHTML = this.actionsMarkup(candidate);
        this.setValues(col, candidate);

        // Trustpilot and the sample box both bind on their own observers, so a
        // fresh column announces itself rather than assuming it was noticed.
        document.dispatchEvent(
          new CustomEvent("compare:column-rendered", { detail: { column, candidate } }),
        );
      }

      /** Writes this candidate's spec values into every row of its column. */
      setValues(col, candidate) {
        this.querySelectorAll(`[data-compare-value][data-compare-col="${col}"]`).forEach((cell) => {
          const key = cell.dataset.compareValue;
          const value = candidate && candidate.specs ? candidate.specs[key] : null;

          if (value) {
            cell.textContent = value;
          } else {
            cell.innerHTML =
              '<span class="product-compare__empty" aria-hidden="true">&mdash;</span>';
          }
        });
      }

      mediaMarkup(candidate) {
        const images = candidate.images || [];
        const src = images[0] || "";
        let markup = src
          ? `<img class="product-compare__image" src="${src}" alt="${this.escape(candidate.title)}" loading="lazy">`
          : "";

        if (images.length > 1) {
          const dots = images
            .map(
              (image, index) =>
                `<button type="button" class="product-compare__dot${index === 0 ? " is-active" : ""}" data-compare-dot="${index}" data-src="${image}" aria-label="Image ${index + 1}"></button>`,
            )
            .join("");
          markup += `<div class="product-compare__dots flex flex-center g-2" data-compare-dots>${dots}</div>`;
        }

        return markup;
      }

      actionsMarkup(candidate) {
        const sample = candidate.sampleId
          ? `<button type="button" class="button-1 product-compare__sample" data-sample-add data-sample-variant-id="${candidate.sampleId}" data-sample-state="default"><span data-sample-label>${this.escape(window.themeStrings?.sampleOrder || "Order Sample")}</span></button>`
          : "";

        return `${sample}<a class="button-text product-compare__shop" href="${candidate.url}">Shop now</a>`;
      }

      placeholderMarkup() {
        return (
          '<button type="button" class="product-compare__placeholder flex flex-col flex-center align-center g-3" data-compare-open>' +
          '<span class="product-compare__plus flex-center align-center" aria-hidden="true">+</span>' +
          '<span class="product-compare__placeholder-text">Select a Product to Compare</span>' +
          "</button>"
        );
      }

      escape(value) {
        const div = document.createElement("div");
        div.textContent = value ?? "";
        return div.innerHTML;
      }
    },
  );
}

/**
 * The colour selector swaps the main product section in place and announces it
 * with product:swapped. Compare lives in its own section, so it re-requests
 * itself for the new product rather than going stale - the candidate list, the
 * spec rows and the locked first column all belong to the old product.
 *
 * Re-rendering rather than patching in place: the row set is built in Liquid
 * from whichever spec fields the product actually has, so a different product
 * is a different table, not just different values.
 */
if (!window.__compareSwapBound) {
  window.__compareSwapBound = true;

  document.addEventListener("product:swapped", async (event) => {
    const element = document.querySelector("product-compare");
    const section = element && element.closest('[id^="shopify-section-"]');
    if (!section) return;

    const url = new URL(event.detail.url, window.location.origin);
    url.searchParams.set("section_id", section.id.replace("shopify-section-", ""));

    section.classList.add("is-loading");

    try {
      const response = await fetch(url.toString());
      if (!response.ok) return;

      const next = new DOMParser()
        .parseFromString(await response.text(), "text/html")
        .querySelector('[id^="shopify-section-"]');

      // No comparison set for the new product - drop the section rather than
      // leaving the previous product's table behind.
      if (!next) {
        section.remove();
        return;
      }

      // Replacing the node upgrades the new <product-compare> on insertion, so
      // there is nothing to rebind by hand.
      section.replaceWith(next);
    } catch {
      section.classList.remove("is-loading");
    }
  });
}
